import React, { useEffect, useMemo } from "react";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, MenuItem, Popover as MuiPopover } from "@material-ui/core";
import styled from "styled-components/macro";

import { StyledInput as Input } from "components/Form/FormikControls/layout";
import { Column, Row } from "components/Layout";
import { BoldText } from "components/Text";

import IconButton from "./IconButton";

const HeaderWrapper = styled(Column)`
  width: 100%;
  padding-left: ${({ theme }) => theme.space[2]}px;
  padding-right: ${({ theme }) => theme.space[2]}px;
`;

const ButtonIcon = styled(FontAwesomeIcon)(
  ({ theme, color }) => `
    color: ${color ? theme.colors[color] : theme.colors.white};
    margin: ${theme.space[0]}px ${theme.space[1]}px ${theme.space[0]}px ${
      theme.space[0]
    }px;
  `,
);

const FilterInput = styled(Input)`
  text-align: center;
  ::placeholder {
    color: ${({ theme }) => theme.colors.primary};
  }
  justify-self: center;
`;

const Popover = styled(({ restrictHeightSubNav: ignored, ...popoverAttrs }) => (
  <MuiPopover {...popoverAttrs} />
))`
  max-height: ${({ restrictHeightSubNav }) =>
    restrictHeightSubNav ? "350px" : "none"};
`;

const Title = styled.span`
  font-weight: ${({ bold }) => (bold ? `bold` : ``)};
`;

export const SubMenuPopover = ({
  subMenuItemsTitle = null,
  subNavItems = [],
  anchorEl,
  includeSearchSubNavThreshold = null,
  restrictHeightSubNav = false,
  open,
  onClose,
}) => {
  const [searchText, setSearchText] = React.useState("");
  const [activeSubButton, setActiveSubButton] = React.useState(null);
  const subNavAnchorRef = React.useRef(null);

  useEffect(() => setSearchText(""), [open]);

  const filteredSubNavItems = useMemo(
    () =>
      subNavItems.filter(item =>
        item.title.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [subNavItems, searchText],
  );

  const handleClose = () => {
    onClose();
    setActiveSubButton(null);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      restrictHeightSubNav={restrictHeightSubNav}
    >
      {includeSearchSubNavThreshold &&
        subNavItems.length > includeSearchSubNavThreshold && (
          <FilterInput
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search"
            onClick={e => e.stopPropagation()}
            autoFocus
          />
        )}
      {subMenuItemsTitle && (
        <HeaderWrapper>
          <Row fullWidth verticalPadding={2} alignCenter justifyBetween>
            <BoldText fontSize="gamma">{subMenuItemsTitle}</BoldText>
            <IconButton
              data-tour="close-sub-menu"
              aria-label="close"
              onClick={handleClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Row>
        </HeaderWrapper>
      )}
      {filteredSubNavItems.map(
        (
          {
            onClick,
            title,
            titleBold,
            icon,
            isDisabled,
            dataTour = "",
            dividerAfter = false,
            iconColor = "black",
            subNavItems = null,
            subMenuItemsTitle = null,
          },
          index,
        ) => (
          <React.Fragment key={title}>
            <MenuItem
              // normal white space allows long titles to wrap
              style={{ whiteSpace: "normal" }}
              onClick={e => {
                if (subNavItems?.length > 0 && activeSubButton === null) {
                  subNavAnchorRef.current = e.currentTarget;
                  setActiveSubButton(index);
                } else if (open && activeSubButton !== null) {
                  setActiveSubButton(null);
                } else if (!isDisabled) {
                  handleClose();
                  onClick && onClick(e);
                }
              }}
              disabled={isDisabled}
              data-tour={dataTour}
            >
              {icon && <ButtonIcon icon={icon} color={iconColor} />}
              <Title bold={titleBold}>{title}</Title>
            </MenuItem>
            {dividerAfter ? <Divider /> : null}
            {Boolean(subNavItems) && (
              <SubMenuPopover
                subNavItems={subNavItems}
                subMenuItemsTitle={subMenuItemsTitle}
                anchorEl={anchorEl}
                onClose={handleClose}
                open={Boolean(activeSubButton === index && subNavItems)}
              />
            )}
          </React.Fragment>
        ),
      )}{" "}
    </Popover>
  );
};
